import { ReactElement, ReactNode } from "react";

import { t } from "i18next";

import Close from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  useTheme
} from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

import { elevatedSurfaceShadow, isDarkMode } from "@/src/theme/helpers";

import { toSxArray } from "@/src/utils/sx";

export type ModalWrapperSize =
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "xxl"
  | "full";
export type ModalWrapperActionLayout = "default" | "stacked";
export type ModalWrapperActionColor =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "error"
  | "info";

export interface ModalWrapperAction {
  id: string;
  label: string;
  onClick: () => void;
  variant?: "contained" | "outlined" | "text";
  color?: ModalWrapperActionColor;
  disabled?: boolean;
  /** Shows an inline spinner and disables the action while true. */
  loading?: boolean;
  startIcon?: ReactNode;
  /** Forces full width regardless of breakpoint (default layout only stretches on mobile). */
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
  /** Renders as the button's `data-testid` attribute for E2E tests. */
  dataTestId?: string;
}

export interface ModalWrapperProps {
  /** Final dialog id (e.g. `draftAuctionModal`). Child elements use `Title`, `Content`, and `CloseButton` suffixes. */
  id: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  /** Accessible name for dialogs without a title (when aria-labelledby is unavailable). */
  ariaLabel?: string;
  size?: ModalWrapperSize;
  children?: ReactNode;
  /** The dialog's main call to action, rendered right-most (or top, in stacked layout). */
  primaryAction?: ModalWrapperAction;
  /** The cancel/close action, rendered left-most (or bottom, in stacked layout). */
  secondaryAction?: ModalWrapperAction;
  /** Rare cases needing more than two actions (e.g. admin list management modals). */
  extraActions?: ModalWrapperAction[];
  /** "stacked" renders full-width vertical actions, used for emphasised decline/confirm flows. */
  actionLayout?: ModalWrapperActionLayout;
  /** Extra icon buttons rendered in a column below the close (X) button. */
  headerButtons?: ReactElement;
  /** When true, the X button and backdrop/escape close are disabled. */
  disableClose?: boolean;
  /** Merged into the Dialog paper's sx (size, spacing, shadow overrides). */
  paperSx?: SxProps<Theme>;
  sx?: SxProps<Theme>;
  /** Renders the dialog in-place instead of a portal (e.g. Storybook docs previews). */
  disablePortal?: boolean;
  /** Hides the backdrop overlay (e.g. Storybook docs previews). */
  hideBackdrop?: boolean;
  /** Prevents the dialog from locking page scroll (e.g. Storybook docs previews). */
  disableScrollLock?: boolean;
  /** Prevents the dialog from stealing focus on open (e.g. Storybook docs previews). */
  disableAutoFocus?: boolean;
  /** Prevents focus from being trapped inside the dialog (e.g. Storybook docs previews). */
  disableEnforceFocus?: boolean;
}

const MAX_WIDTHS: Record<ModalWrapperSize, string> = {
  xs: "20rem",
  sm: "30rem",
  md: "40rem",
  lg: "50rem",
  xl: "60rem",
  xxl: "80rem",
  full: "100%"
};

const DEFAULT_ACTIONS_SX: SxProps<Theme> = {
  flexDirection: { xs: "column-reverse", sm: "row" },
  gap: 1.5,
  px: 3,
  py: 2,
  justifyContent: { xs: "stretch", sm: "flex-end" },
  "& > button": {
    width: { xs: "100%", sm: "auto" }
  }
};

const STACKED_ACTIONS_SX: SxProps<Theme> = {
  flexDirection: "column",
  gap: 1.5,
  px: 3,
  py: 2,
  alignItems: "stretch",
  "& > button": {
    width: "100%"
  }
};

const getModalContentSx =
  (hasTitleArea: boolean) =>
  (muiTheme: Theme): SystemStyleObject<Theme> => ({
    px: 3,
    pt: hasTitleArea ? 0 : 2,
    pb: 2.5,
    color: muiTheme.palette.text.primary
  });

const PRIMARY_ACTION_DEFAULTS: Pick<ModalWrapperAction, "variant" | "color"> = {
  variant: "contained",
  color: "primary"
};

const SECONDARY_ACTION_DEFAULTS: Pick<ModalWrapperAction, "variant" | "color"> =
  {
    variant: "text",
    color: "primary"
  };

const EXTRA_ACTION_DEFAULTS: Pick<ModalWrapperAction, "variant"> = {
  variant: "text"
};

type ModalWrapperActionRole = "primary" | "secondary" | "extra";

interface ResolvedModalWrapperAction extends ModalWrapperAction {
  role: ModalWrapperActionRole;
}

const getModalPaperSx = (
  muiTheme: Theme,
  hasCustomPaperWidth: boolean,
  size: ModalWrapperSize
): SystemStyleObject<Theme> => ({
  width: "100%",
  m: 2,
  ...(hasCustomPaperWidth ? {} : { maxWidth: MAX_WIDTHS[size] }),
  minHeight: "3.5rem",
  color: muiTheme.palette.text.primary,
  ...(isDarkMode(muiTheme)
    ? {
        boxShadow: elevatedSurfaceShadow(muiTheme)
      }
    : {})
});

const getBackdropSx = (muiTheme: Theme): SystemStyleObject<Theme> => ({
  backgroundColor: muiTheme.palette.misc.backdropOverlay
});

const getSecondaryActionSx = (muiTheme: Theme): SxProps<Theme> =>
  isDarkMode(muiTheme)
    ? {
        color: muiTheme.palette.text.primary,
        "&:hover": {
          backgroundColor: muiTheme.palette.action.hover
        }
      }
    : {
        color: muiTheme.palette.semantic.primary.main,
        "&:hover": {
          backgroundColor: muiTheme.palette.semantic.primary.hover
        }
      };

const getExtraActionSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.primary,
  "&:hover": {
    backgroundColor: theme.palette.action.hover
  }
});

const getRoleActionSx = (
  role: ModalWrapperActionRole,
  theme: Theme
): SxProps<Theme> | undefined => {
  if (role === "secondary") {
    return getSecondaryActionSx(theme);
  }

  if (role === "extra") {
    return getExtraActionSx(theme);
  }

  return undefined;
};

const withActionDefaults = (
  action: ModalWrapperAction,
  defaults: Pick<ModalWrapperAction, "variant" | "color">
): ModalWrapperAction => ({
  ...action,
  variant: action.variant ?? defaults.variant,
  color: action.color ?? defaults.color
});

const withExtraActionDefaults = (
  action: ModalWrapperAction
): ModalWrapperAction => ({
  ...action,
  variant: action.variant ?? EXTRA_ACTION_DEFAULTS.variant
});

const resolveAction = (
  action: ModalWrapperAction | undefined,
  role: ModalWrapperActionRole,
  defaults: Pick<ModalWrapperAction, "variant" | "color">
): ResolvedModalWrapperAction | undefined => {
  if (!action) {
    return undefined;
  }

  return {
    ...withActionDefaults(action, defaults),
    role
  };
};

const resolveExtraAction = (
  action: ModalWrapperAction
): ResolvedModalWrapperAction => ({
  ...withExtraActionDefaults(action),
  role: "extra"
});

const getActionsInOrder = (
  actionLayout: ModalWrapperActionLayout,
  primaryAction?: ResolvedModalWrapperAction,
  secondaryAction?: ResolvedModalWrapperAction,
  extraActions: ResolvedModalWrapperAction[] = []
): ResolvedModalWrapperAction[] => {
  const orderedActions =
    actionLayout === "stacked"
      ? [primaryAction, ...extraActions, secondaryAction]
      : [secondaryAction, ...extraActions, primaryAction];

  return orderedActions.filter((action): action is ResolvedModalWrapperAction =>
    Boolean(action)
  );
};

const definesPaperWidth = (value: unknown): boolean => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const styles = value as Record<string, unknown>;

  if ("width" in styles || "maxWidth" in styles) {
    return true;
  }

  const paperStyles = styles["& .MuiDialog-paper"];

  if (paperStyles && typeof paperStyles === "object") {
    const paper = paperStyles as Record<string, unknown>;

    return "width" in paper || "maxWidth" in paper;
  }

  return false;
};

const sxDefinesPaperWidth = (customSx?: SxProps<Theme>): boolean => {
  if (!customSx) {
    return false;
  }

  const entries = Array.isArray(customSx) ? customSx : [customSx];

  return entries.some((entry) => {
    if (typeof entry === "function") {
      return false;
    }

    return definesPaperWidth(entry);
  });
};

const renderAction = (action: ResolvedModalWrapperAction, theme: Theme) => {
  const {
    id: actionId,
    label,
    onClick,
    variant,
    color,
    disabled,
    loading,
    startIcon,
    fullWidth,
    sx,
    dataTestId,
    role
  } = action;
  const isDisabled = disabled || loading;
  const roleSx = getRoleActionSx(role, theme);
  const mergedSx: SxProps<Theme> | undefined = roleSx
    ? ([roleSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>)
    : sx;

  return (
    <Button
      id={actionId}
      key={actionId}
      data-testid={dataTestId}
      variant={variant}
      color={color}
      disabled={isDisabled}
      fullWidth={fullWidth}
      startIcon={
        loading ? <CircularProgress size={16} color="inherit" /> : startIcon
      }
      onClick={() => {
        if (!isDisabled) {
          onClick();
        }
      }}
      sx={mergedSx}
    >
      {label}
    </Button>
  );
};

const ModalWrapper = (props: ModalWrapperProps) => {
  const {
    id,
    open,
    onClose,
    title,
    ariaLabel,
    size = "md",
    children,
    primaryAction,
    secondaryAction,
    extraActions,
    actionLayout = "default",
    headerButtons,
    disableClose = false,
    paperSx,
    sx,
    disablePortal,
    hideBackdrop,
    disableScrollLock,
    disableAutoFocus,
    disableEnforceFocus
  } = props;
  const theme = useTheme();

  const hasDescribedContent =
    children != null && (!Array.isArray(children) || children.length > 0);
  const normalizedExtraActions = extraActions?.map(resolveExtraAction);
  const actions = getActionsInOrder(
    actionLayout,
    resolveAction(primaryAction, "primary", PRIMARY_ACTION_DEFAULTS),
    resolveAction(secondaryAction, "secondary", SECONDARY_ACTION_DEFAULTS),
    normalizedExtraActions
  );
  const hasCustomPaperWidth =
    sxDefinesPaperWidth(paperSx) || sxDefinesPaperWidth(sx);
  const hasTitleArea = !!title || !disableClose || !!headerButtons;

  return (
    <Dialog
      id={id}
      open={open}
      onClose={disableClose ? undefined : onClose}
      disablePortal={disablePortal}
      hideBackdrop={hideBackdrop}
      disableScrollLock={disableScrollLock}
      disableAutoFocus={disableAutoFocus}
      disableEnforceFocus={disableEnforceFocus}
      maxWidth={hasCustomPaperWidth ? false : undefined}
      aria-labelledby={title ? `${id}Title` : undefined}
      aria-label={!title ? ariaLabel : undefined}
      aria-describedby={hasDescribedContent ? `${id}Content` : undefined}
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: getBackdropSx
        },
        paper: {
          sx: [
            (muiTheme: Theme) =>
              getModalPaperSx(muiTheme, hasCustomPaperWidth, size),
            ...toSxArray(paperSx)
          ]
        }
      }}
      sx={sx}
    >
      {hasTitleArea && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 3,
            pt: 2,
            pb: 2
          }}
        >
          {title ? (
            <DialogTitle
              id={`${id}Title`}
              sx={{
                p: 0,
                mb: 0,
                flex: 1,
                display: "flex",
                alignItems: "center",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                color: "text.primary"
              }}
            >
              {title}
            </DialogTitle>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          {(!disableClose || !!headerButtons) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
                ml: 1
              }}
            >
              {!disableClose && (
                <IconButton
                  id={`${id}CloseButton`}
                  aria-label={t("general.close")}
                  onClick={onClose}
                  sx={{ color: "text.primary" }}
                >
                  <Close />
                </IconButton>
              )}
              {headerButtons}
            </Box>
          )}
        </Box>
      )}
      <DialogContent id={`${id}Content`} sx={getModalContentSx(hasTitleArea)}>
        {children}
      </DialogContent>
      {actions.length > 0 && (
        <DialogActions
          sx={
            actionLayout === "stacked" ? STACKED_ACTIONS_SX : DEFAULT_ACTIONS_SX
          }
        >
          {actions.map((action) => renderAction(action, theme))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ModalWrapper;
